import { useCallback, useEffect, useMemo, useState } from "react";
import { SliceDirection, type SliceOpt } from "@b2bportal/core-types";
import { getSliceDirection } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { FlightSlicePickerModalContentProps } from "@tenant/flights";
import { uniqueId } from "lodash-es";

export const useFlightSlicePickerModalContent = (
  props: FlightSlicePickerModalContentProps
) => {
  const { flight, isOutboundDisabled, isReturnDisabled, onSliceToggle } = props;
  const { locators, slices } = flight.bookedItinerary.travelItinerary;
  const { t } = useI18nContext();
  const [sliceOpts, setSliceOpts] = useState<SliceOpt[]>([]);

  const onSliceCheckedChange = useCallback(
    (sliceId: string, isChecked: boolean) => {
      const sliceIdx = sliceOpts.findIndex((opt) => opt.id === sliceId);

      if (sliceIdx === -1 || sliceOpts[sliceIdx].disabled) return;

      setSliceOpts([
        ...sliceOpts.slice(0, sliceIdx),
        { ...sliceOpts[sliceIdx], checked: isChecked },
        ...sliceOpts.slice(sliceIdx + 1),
      ]);
      onSliceToggle?.(sliceIdx, isChecked);
    },
    [onSliceToggle, sliceOpts]
  );

  useEffect(() => {
    const newSliceOpts = slices.map<SliceOpt>((slice) => {
      const direction = getSliceDirection(slice, slices);
      const isDisabled =
        (direction === SliceDirection.Outbound && isOutboundDisabled) ||
        (direction === SliceDirection.Return && isReturnDisabled);

      return {
        direction,
        slice,
        checked: isDisabled,
        disabled: isDisabled,
        locator: locators?.b2b ?? "",
        id: uniqueId("slice-opt-"),
      };
    });

    setSliceOpts(newSliceOpts);
  }, [isOutboundDisabled, isReturnDisabled, locators?.b2b, slices]);

  return useMemo(
    () => ({
      context: {
        sliceOpts,
        confirmBtnEnabled: sliceOpts.some(
          (opt) => !opt.disabled && opt.checked
        ),
      },
      handlers: {
        onSliceCheckedChange,
      },
      text: {
        confirmBtn: t("core-flights.exchange.slicePicker.confirmButtonLabel"),
        outboundSliceTitle: t("outbound"),
        modalTitle: t(
          "core-flights.exchange.slicePicker.chooseFlightsModalTitle"
        ),
        returnSliceTitle: t("return"),
      },
    }),
    [onSliceCheckedChange, sliceOpts, t]
  );
};

export type ISlicePickerModalContentData = ReturnType<
  typeof useFlightSlicePickerModalContent
>;
